import { useAuth0 } from "@auth0/auth0-react";
import { Building, Location, Scale } from "@carbon/icons-react";
import { Office } from "@carbon/pictograms-react";
import {
  Dropdown, SkeletonPlaceholder, SkeletonText, Tab, TabList, TabPanel, TabPanels,
  Tabs, Tag, Toggle
} from "@carbon/react";
import axios from "axios";
import Color from "color";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import BarChart from "../../components/BarChart";
import LineChart from "../../components/LineChart";
import PageTitleBlock from "../../components/PageTitleBlock/PageTitleBlock";
import SelectReportDate from "../../components/SelectReportDate/SelectReportDate";
import ReportDateContext from "../../ReportDateContext";

import wspColor from "../../wspColours.scss";

const BuildingReport = () => {
  const { getAccessTokenSilently } = useAuth0();

  const location = useLocation();
  const navigate = useNavigate();
  const { year, month, useActual, selectedReport, availableReports, setReportDate, setUseActual } = useContext(ReportDateContext);
  const [loading, setLoading] = useState(true);
  const [selectedReportStart, setSelectedReportStart] = useState(selectedReport);
  const [availableStartReports, setAvailableStartReports] = useState([]);
  const [buildingDetails, setBuildingDetails] = useState({});
  const [infoBits, setInfoBits] = useState([]);
  const [euiSummary, setEuiSummary] = useState([]);
  const [wuiSummary, setWuiSummary] = useState([]);
  const [usageChange, setUsageChange] = useState({});
  const [utilitiesUsage, setUtilitiesUsage] = useState([]);
  const [quarterlyUsage, setQuarterlyUsage] = useState([]);
  const [degreeDays, setDegreeDays] = useState({});
  const [degreeDaysChange, setDegreeDaysChange] = useState({});
  const [rollingEuiData, setRollingEuiData] = useState([]);
  const [rollingWuiData, setRollingWuiData] = useState([]);
  const [analyzeTimePeriod, setAnalyzeTimePeriod] = useState(false);
  const [selectionEstimatedFlag, setSelectionEstimatedFlag] = useState(false);
  const [hasNormalizedFlag, setHasNormalizedFlag] = useState(true);

  if (year === null && month === null) {
    navigate("/"); // redirect to main page
  }

  const usageUnits = {
    all: "ekWh",
    combined: "ekWh",
    electricity: "kWh",
    natural_gas: "cu. m",
    steam: "lb",
    water: "cu. m",
    chilled_water: "ton-hr"
  };

  useEffect(() => {
    const chartColors = [Color(wspColor.red01), Color(wspColor.charcoaldark), Color(wspColor.blue)];
    const validBits = [
      { key: "gfa", label: "GFA (sq. ft)", icon: <Scale size={16} />, format: true },
      { key: "floors", label: "Floors", icon: <Building size={16} /> },
      { key: "year_built", label: "Built", icon: <Building size={16} /> },
      { key: "year_renovated", label: "Last Renovated", icon: <Building size={16} /> },
      { key: "address", label: "Address", icon: <Location size={16} /> }
    ];

    const formatUsageChartData = utilityUsage =>
      Object.entries(utilityUsage).map(([utility, usage]) => ({
        labels: [utility],
        datasets: Object.entries(usage).map(([period, value], index) => ({
          label: period,
          data: [value],
          backgroundColor: chartColors[index].alpha(0.5).string(),
          borderColor: chartColors[index].string(),
          borderWidth: 1
        }))
      }));

    const formatUiChartData = (uiData, portfolioUiData) =>
      Object.entries(uiData.values).map(([key, value]) => ({
        labels: uiData.labels,
        datasets: [
          {
            label: key,
            data: value,
            backgroundColor: chartColors[0].alpha(0.5).string(),
            borderColor: chartColors[0].string(),
            borderWidth: 1,
            fill: "start",
            tension: 0.2
          },
          {
            label: "Portfolio Avg",
            data: portfolioUiData.values[key],
            backgroundColor: chartColors[1].string(),
            borderColor: chartColors[1].string(),
            borderWidth: 1,
            borderDash: [5, 5],
            fill: false,
            tension: 0.2
          }
        ]
      }));

    const getData = async () => {
      try {
        setLoading(true);
        const accessToken = await getAccessTokenSilently();
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_SERVER_URL}/data/building_report/${location.state.building}/${selectedReportStart.year}/${selectedReportStart.month}/${year}/${month}${useActual ? "?actual" : ""}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );

        // Set page data
        setBuildingDetails(data.building_details);
        setInfoBits(
          validBits.map(item =>
            Object.assign(item, {
              value: item.format
                ? data.building_details[item.key].toLocaleString()
                : data.building_details[item.key]
            })
          )
        );
        setEuiSummary([
          {
            label: "Site (Current)",
            value: data.eui_curr
          },
          {
            label: `Site (${parseInt(year, 10) - 1})`,
            value: data.eui_prev
          },
          {
            label: "Portfolio (Current)",
            value: data.rolling_eui_portfolio.values.Combined.slice(-1)[0]
          }
        ]);
        setWuiSummary([
          {
            label: "Site (Current)",
            value: data.wui_curr
          },
          {
            label: `Site (${parseInt(year, 10) - 1})`,
            value: data.wui_prev
          },
          {
            label: "Portfolio (Current)",
            value: data.rolling_wui_portfolio.values.Water.slice(-1)[0]
          }
        ]);
        setUsageChange({
          labels: data.usage_change.utility_type,
          datasets: [
            {
              label: "Change from previous year",
              data: data.usage_change.rel_change,
              backgroundColor: chartColors[0].alpha(0.5).string(),
              borderColor: chartColors[0].string(),
              borderWidth: 1
            }
          ]
        });
        setUtilitiesUsage(formatUsageChartData(data.usage));
        setQuarterlyUsage(formatUsageChartData(data.qtr_usage));
        setDegreeDays({
          labels: data.degree_days.label,
          datasets: [
            {
              label: "HDD",
              data: data.degree_days.hdd,
              backgroundColor: chartColors[0].alpha(0.5).string(),
              borderColor: chartColors[0].string(),
              borderWidth: 1
            },
            {
              label: "CDD",
              data: data.degree_days.cdd,
              backgroundColor: chartColors[1].alpha(0.5).string(),
              borderColor: chartColors[1].string(),
              borderWidth: 1
            }
          ]
        });
        setDegreeDaysChange({
          labels: ["Change from previous year"],
          datasets: [
            {
              label: "HDD",
              data: data.degree_days_change.hdd,
              backgroundColor: chartColors[0].alpha(0.5).string(),
              borderColor: chartColors[0].string(),
              borderWidth: 1
            },
            {
              label: "CDD",
              data: data.degree_days_change.cdd,
              backgroundColor: chartColors[1].alpha(0.5).string(),
              borderColor: chartColors[1].string(),
              borderWidth: 1
            }
          ]
        });
        setRollingEuiData(formatUiChartData(data.rolling_eui, data.rolling_eui_portfolio));
        setRollingWuiData(formatUiChartData(data.rolling_wui, data.rolling_wui_portfolio));
        setSelectionEstimatedFlag(data.selection_estimated);
        setHasNormalizedFlag(data.normalized);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const startDate = new Date(selectedReportStart.year, selectedReportStart.month);
    const endDate = new Date(year, month);
    if (startDate <= endDate) {
      const filteredStartReports = availableReports.filter(d => {
        const reportDate = new Date(d.year, d.month);
        return reportDate <= endDate;
      });
      setAvailableStartReports(filteredStartReports);
      getData();
    } else {
      setAnalyzeTimePeriod(false);
      setSelectedReportStart(selectedReport);
    }
  }, [location, month, year, useActual, selectedReport, selectedReportStart, availableReports, getAccessTokenSilently]);

  const changeTimePeriodToggle = toggleState => {
    setAnalyzeTimePeriod(toggleState);
    if (!toggleState) {
      setSelectedReportStart(selectedReport); // reset to end date
    }
  };

  /*
   * Page rendering functions
   */
  const createUseIntensityVals = (title, summaryValues) => (
    <div>
      <h5>{title}</h5>
      <div>
        {loading ? (
          <div>
            <SkeletonText paragraph lineCount={2} />
          </div>
        ) : (
          summaryValues.map((d, i) =>
            (d.value > 0) && (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${title}-summary-${i}`}>
                <h6>{d.label}</h6>
                <p>{d.value}</p>
              </div>
            )
          )
        )}
      </div>
    </div>
  );

  const createUiChartTabs = (tabChartData, unitLabel) => (
    <Tabs>
      <TabList aria-label="List of tabs">
        {tabChartData.map(d => <Tab key={`tab-${d.datasets[0].label}`}>{d.datasets[0].label}</Tab>)}
      </TabList>
      <TabPanels>
        {tabChartData.map(d => (
          <TabPanel
            tabIndex={0}
            label={d.datasets[0].label}
            key={`tabbed-chart-${d.datasets[0].label}`}
          >
            <div>
              <LineChart
                chartId={`building-report-linechart-ui-${d.datasets[0].label}`}
                chartData={d}
                aspectRatio={1.5}
                labelYAxis="Use Intensity"
                unitYAxis={unitLabel}
                legend
                startYAtZero
              />
            </div>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );

  return (
    <>
      <SelectReportDate
        id="selectedReportDate"
        availableReports={availableReports}
        selectedReport={selectedReport}
        label="Available Reports"
        onChange={setReportDate}
        useActual={useActual}
        onChangeuseActual={setUseActual}
        light
      />

      <PageTitleBlock
        title={buildingDetails.bname}
        subtitle={buildingDetails.group}
        icon={<Office />}
        breadcrumbs={[
          {
            label: "UBI Dashboard",
            url: "/"
          },
          {
            label: `${buildingDetails.group} Portfolio`,
            url: "/building_portfolio",
            state: { group: buildingDetails.groupid }
          }
        ]}
        infoBits={infoBits}
        loading={loading}
      />

      <section className="cds--grid">
        <div className="cds--row">
          <div className="cds--col-lg-3 cds--col-md-8 building-report__building_summary">
            <div>
              <div>
                <h3>Summary</h3>
                <p>{useActual ? "Actual" : hasNormalizedFlag ? "Normalized" : "Actual"}</p>
              </div>
              <div>
                {createUseIntensityVals("EUI", euiSummary)}
                {createUseIntensityVals("WUI", wuiSummary)}
              </div>
            </div>
          </div>

          <div className="cds--col-lg-8 cds--col-md-8 building-report__building_usage_main">
            <div>
              <div className="cds--row building-report__usage_heading">
                <div className="cds--col-lg-16 cds--col-md-8">
                  <h3>Usage Details</h3>
                  <h4>
                    {analyzeTimePeriod
                      ? `From ${selectedReportStart.label} to ${selectedReport.label}`
                      : `For ${selectedReport.label}`}
                  </h4>
                  <div className="building-report__usage_tags">
                    {useActual ? null : hasNormalizedFlag ? (
                      <Tag type="cool-gray">
                        Values normalized using client-provided coefficients
                      </Tag>
                    ) : (
                      <Tag type="purple">
                        Normalized values unavailable, reporting actual
                      </Tag>
                    )}
                    {selectionEstimatedFlag && (
                      <Tag type="cyan">
                        Current selection contains estimated values
                      </Tag>
                    )}
                  </div>
                  <div>
                    <Toggle
                      aria-label="Toggle to show usage over a time period"
                      id="compare-time-period"
                      labelText="Show usage over a time period"
                      labelA="Show usage over a time period"
                      labelB="Show usage over a time period"
                      toggled={analyzeTimePeriod}
                      onToggle={value => changeTimePeriodToggle(value)}
                      size="sm"
                      hideLabel
                    />
                  </div>
                  {analyzeTimePeriod && (
                    <div className="building-report__select_usage_range">
                      <Dropdown
                        id="selectedReportStartDate"
                        items={availableStartReports}
                        selectedItem={selectedReportStart}
                        label="Available Reports"
                        titleText="From"
                        onChange={({ selectedItem }) => setSelectedReportStart(selectedItem)}
                      />
                      <Dropdown
                        id="selectedReportEndDate1"
                        items={availableReports}
                        selectedItem={selectedReport}
                        label="Available Reports"
                        titleText="To"
                        disabled
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="cds--row">
                <div className="cds--col-lg-16 cds--col-md-8">
                  <h5>Year-Over-Year Utility Consumption</h5>
                  {loading ? (
                    <SkeletonPlaceholder className="building-report__skeleton_placeholder" />
                  ) : (
                    <BarChart
                      chartId="building-report-barchart-yoy-consumption"
                      chartData={usageChange}
                      aspectRatio={2}
                      labelYAxis="Percent Change"
                      percentYAxis
                      legend
                    />
                  )}
                </div>
              </div>
              <div className="cds--row">
                {loading
                  ? null
                  : utilitiesUsage.map(d => (
                    // eslint-disable-next-line
                    <div className="cds--col-lg-8 cds--col-md-4" key={`usage-${d.labels[0].toLowerCase().replace(" ", "_")}`}>
                      <h5>{d.labels[0]}</h5>
                      <BarChart
                        // eslint-disable-next-line
                        chartId={`building-report-barchart-consumption-${d.labels[0].toLowerCase().replace(" ", "_")}`}
                        chartData={d}
                        labelYAxis="Consumption"
                        unitYAxis={
                          usageUnits[
                          d.labels[0].toLowerCase().replace(" ", "_")
                          ]
                        }
                        legend
                      />
                    </div>
                  ))}
              </div>
              <div className="cds--row building-report__weather_heading">
                <div className="cds--col-lg-16 cds--col-md-8">
                  <h4>Weather</h4>
                  {loading ? (
                    <SkeletonText paragraph lineCount={5} />
                  ) : (
                    <p>
                      Degree Days measure how cold or warm a location is by
                      comparing the average monthly temperature to a balance
                      point (18°C). Cooling Degree Days (CDD) quantifies the
                      number of degrees that a month&apos;s average
                      temperature is above the balance point, such that the
                      building will need to be cooled. Heating Degree Days
                      (HDD) quantifies the number of degrees that a
                      month&apos;s average temperature is below the balance
                      point, such that the building will need to be heated.
                    </p>
                  )}
                </div>
              </div>
              <div className="cds--row">
                <div className="cds--col-lg-8 cds--col-md-4">
                  <h5>Year-Over-Year Degree Days</h5>
                  {loading ? (
                    <SkeletonPlaceholder className="building-report__skeleton_placeholder" />
                  ) : (
                    <BarChart
                      chartId="building-report-barchart-yoy-weather"
                      chartData={degreeDays}
                      labelYAxis="Degree Days"
                      legend
                    />
                  )}
                </div>
                <div className="cds--col-lg-8 cds--col-md-4">
                  <h5>% Change in Degree Days</h5>
                  {loading ? (
                    <SkeletonPlaceholder className="building-report__skeleton_placeholder" />
                  ) : (
                    <BarChart
                      chartId="building-report-barchart-yoy-weather-change"
                      chartData={degreeDaysChange}
                      labelYAxis="Percent Change"
                      percentYAxis
                      legend
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="cds--col-lg-5 building-report__building_usage_secondary">
            <div>
              <h3>Rolling 12-Month EUI</h3>
              {loading ? (
                <SkeletonPlaceholder className="building-report__skeleton_placeholder" />
              ) : (
                createUiChartTabs(rollingEuiData, "ekWh/sq ft")
              )}
            </div>
            <div>
              <h3>Rolling 12-Month WUI</h3>
              {loading ? (
                <SkeletonPlaceholder className="building-report__skeleton_placeholder" />
              ) : (
                createUiChartTabs(rollingWuiData, "L/sq ft")
              )}
            </div>
            <div>
              <h3>Quarterly Usage</h3>
              {loading ? (
                <SkeletonPlaceholder className="building-report__skeleton_placeholder" />
              ) : (
                <Tabs>
                  <TabList aria-label="List of tabs">
                    {quarterlyUsage.map(d => <Tab key={`tab-${d.labels[0]}`}>{d.labels[0]}</Tab>)}
                  </TabList>
                  <TabPanels>
                    {quarterlyUsage.map(d => (
                      <TabPanel
                        tabIndex={0}
                        label={d.labels[0]}
                        key={`qtr-usage-${d.labels[0]}`}
                      >
                        <div>
                          <BarChart
                            chartId={`building-report-barchart-quarterlyusage-${d.labels[0]}`}
                            chartData={d}
                            aspectRatio={2}
                            labelYAxis="Consumption"
                            unitYAxis={
                              usageUnits[
                              d.labels[0].toLowerCase().replace(" ", "_")
                              ]
                            }
                            legend
                          />
                        </div>
                      </TabPanel>
                    ))}
                  </TabPanels>
                </Tabs>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BuildingReport;
